import { Flex } from 'common/src/designSystem/components/flex';
import {
    AccreditationDisplay,
    AccreditationsSlotId,
    DelegationAccreditationSlotFragment,
    FormAccreditationsOptionsFragment,
    FormRegisterAccreditationFragment,
    VolunteersRegistrationsSlotInput
} from 'common/src/generated/types';
import { addOrRemove, isNonEmptyArray } from 'common/src/util/array';
import { isNonEmptyString } from 'common/src/util/string';
import { canSelect } from 'common/src/vo/accreditationSlot';
import { compact, intersection, noop, sortBy } from 'lodash-es';
import * as React from 'react';
import { Accreditation } from '../../../accreditations/v2/accreditation';
import { UserCreateField } from '../../../components/users/create/userCreateField';
import { Button } from '../../../designSystem/components/button';
import { Checkbox } from '../../../designSystem/components/checkbox';
import { useHeavent } from '../../../hooks/useHeavent';
import { FormMissionDescriptionModal } from '../../missions/formMissionDescriptionModal';
import { FormAccreditationsSlotsCalendars } from './formAccreditationsSlotsCalendars';
import { FormAccreditationsSlotsList } from './formAccreditationsSlotsList';
import { FormAccreditationsSlotsRadio } from './formAccreditationsSlotsRadio';

interface IFormAccreditationsSlotsAccreditationProps {
    accreditation: FormRegisterAccreditationFragment;
    accreditationsSlotsIds: AccreditationsSlotId[];
    delegationAccreditations: DelegationAccreditationSlotFragment[];
    displayFormCustomFields: boolean;
    options: FormAccreditationsOptionsFragment;
    prefix: string;
    slots: VolunteersRegistrationsSlotInput[];

    change(name: string, value: any): void;
}

export const FormAccreditationsSlotsAccreditation = (
    props: IFormAccreditationsSlotsAccreditationProps
) => {
    const {
        params: { organizationId },
        translate
    } = useHeavent();
    const [isDescriptionOpen, setIsDescriptionOpen] = React.useState(false);
    const slots = React.useMemo(
        () =>
            sortBy(
                props.accreditation.slots.filter((slot) =>
                    canSelect(slot, props.slots, props.options, props.delegationAccreditations)
                ),
                (as) => compact([as.date?.toMillis(), as.name.toLowerCase()])
            ),
        [props.accreditation.slots, props.delegationAccreditations, props.slots, props.options]
    );
    const showDescription = React.useMemo(
        () =>
            isNonEmptyString(props.accreditation.description) &&
            props.options.showAccreditationDescription,
        [props.accreditation, props.options]
    );

    const showFormCustomFields = React.useMemo(() => {
        const slotsIds = props.accreditation.hasSlots
            ? slots.map((s) => s.id)
            : [props.accreditation.hiddenSlotId];

        return (
            props.displayFormCustomFields &&
            isNonEmptyArray(props.accreditation.formCustomFields) &&
            isNonEmptyArray(intersection(slotsIds, props.accreditationsSlotsIds))
        );
    }, [props.accreditation, props.accreditationsSlotsIds, props.displayFormCustomFields, slots]);
    const hiddenSlotState = props.accreditationsSlotsIds.includes(props.accreditation.hiddenSlotId)
        ? 'checked'
        : 'unchecked';

    return (
        <>
            <Accreditation
                accreditation={props.accreditation}
                displayFormCustomFields={showFormCustomFields}
                displayNumberOfSlots={false}
                displaySlots={props.accreditation.hasSlots}
                numberOfSlots={0}
                renderActions={() =>
                    showDescription && (
                        <Button
                            color="white"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.nativeEvent.stopImmediatePropagation();

                                setIsDescriptionOpen(true);
                            }}
                        >
                            {translate('voir_la_descrip_62304')}
                        </Button>
                    )
                }
                renderCheckbox={() =>
                    !props.accreditation.hasSlots && (
                        <Checkbox
                            shouldPreventDefault={false}
                            state={hiddenSlotState}
                            onClick={noop}
                        />
                    )
                }
                onClick={() => {
                    if (!props.accreditation.hasSlots) {
                        props.change(
                            `${props.prefix}accreditationsSlotsIds`,
                            addOrRemove(
                                props.accreditationsSlotsIds,
                                props.accreditation.hiddenSlotId,
                                hiddenSlotState === 'unchecked'
                            )
                        );
                    }
                }}
            >
                <Flex
                    css={{
                        background: '$gray50',
                        padding: '$4'
                    }}
                    direction="column"
                    gap="3"
                >
                    {props.accreditation.hasSlots ? (
                        props.accreditation.accreditationDisplay ===
                        AccreditationDisplay.Calendar ? (
                            <FormAccreditationsSlotsCalendars
                                accreditationsSlotsIds={props.accreditationsSlotsIds}
                                change={props.change}
                                prefix={props.prefix}
                                slots={slots}
                            />
                        ) : (
                            <Flex
                                css={{
                                    background: 'white',
                                    bd: true,
                                    padding: '$4'
                                }}
                                direction="column"
                                gap="3"
                                width={1}
                            >
                                {props.accreditation.accreditationDisplay ===
                                AccreditationDisplay.Radio ? (
                                    <FormAccreditationsSlotsRadio
                                        accreditation={props.accreditation}
                                        accreditationsSlotsIds={props.accreditationsSlotsIds}
                                        change={props.change}
                                        prefix={props.prefix}
                                        slots={slots}
                                    />
                                ) : (
                                    <FormAccreditationsSlotsList
                                        accreditation={props.accreditation}
                                        accreditationsSlotsIds={props.accreditationsSlotsIds}
                                        change={props.change}
                                        prefix={props.prefix}
                                        slots={slots}
                                    />
                                )}
                            </Flex>
                        )
                    ) : null}

                    {showFormCustomFields && (
                        <Flex
                            css={{
                                background: 'white',
                                bd: true,
                                padding: '$4'
                            }}
                            direction="column"
                            gap="3"
                            width={1}
                        >
                            {props.accreditation.formCustomFields.map(
                                ({ customField, isMandatory }) => (
                                    <UserCreateField
                                        key={customField.id}
                                        change={props.change}
                                        display={true}
                                        field={customField}
                                        isMandatory={isMandatory}
                                        organizationId={organizationId}
                                        prefix={`${props.prefix}userInfo.fields.`}
                                    />
                                )
                            )}
                        </Flex>
                    )}
                </Flex>
            </Accreditation>

            {isDescriptionOpen && (
                <FormMissionDescriptionModal
                    description={props.accreditation.description}
                    name={props.accreditation.name}
                    onClose={() => {
                        setIsDescriptionOpen(false);
                    }}
                />
            )}
        </>
    );
};
